import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { AppIssue, AppSupportRequest, Attachment, AttachmentsResponse } from '@playq/services-beetle';
import { TimePrecision } from '@playq/octopus-common';

import { snackbarService } from '/common/snackbarService';
import { timeUnitPrecisionMillisecondValues } from '/constants';

interface IRefreshingState {
  id: string;
  action: 'preview' | 'download';
}
const tokenExpirationThreshold = timeUnitPrecisionMillisecondValues[TimePrecision.Minute] * 13;

export const useAttachmentsExpired = ({
  updatedAt,
  queryKeys,
}: {
  updatedAt: number;
  queryKeys: readonly unknown[];
}) => {
  const [isRefreshingAttachment, setIsRefreshingAttachment] = useState<IRefreshingState | null>(null);

  const queryClient = useQueryClient();

  const getFreshAttachment = async ({
    currentAttachment,
    actionType,
    notify = false,
  }: {
    currentAttachment: Attachment;
    actionType: IRefreshingState['action'];
    notify?: boolean;
  }): Promise<Attachment | null> => {
    const isDataOutdated = Date.now() - updatedAt > tokenExpirationThreshold;

    if (!isDataOutdated) {
      return currentAttachment;
    }
    if (notify) {
      snackbarService.warning('Data expired. Updating...');
    }
    setIsRefreshingAttachment({ id: currentAttachment.id.id, action: actionType });

    try {
      await queryClient.invalidateQueries({
        queryKey: queryKeys,
        refetchType: 'active',
      });

      const foundQueries = queryClient.getQueriesData<AppIssue | AppSupportRequest | AttachmentsResponse>({
        queryKey: queryKeys,
      });

      const [firstMatchingQuery] = foundQueries;
      const [_, freshData] = firstMatchingQuery ?? [];

      const freshAttachment = freshData?.attachments.find((att) => att.id.id === currentAttachment.id.id);

      if (!freshAttachment) {
        snackbarService.error('Attachment not found after update');
        return null;
      }
      if (notify) {
        snackbarService.success('Data updated. Downloading...');
      }
      return freshAttachment;
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      snackbarService.error('Failed to update data', details);
      return null;
    } finally {
      setIsRefreshingAttachment(null);
    }
  };
  return { getFreshAttachment, isRefreshingAttachment };
};
